import { action, makeObservable, observable, runInAction } from 'mobx';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';
import { CARD_OWNER_TYPE } from '../enums/cardStatus';
import { CARD_HAND_TYPES, CUTTING_CARD_TYPE } from '../enums/cardTypes';
import { roundActiveHandStore } from './roundActiveHandStore';
import {
  DECISION_ACTION_NAMES_FOR_STORE,
  DECISION_ACTION_TYPES_FOR_STORE,
} from '../enums/decisionTypes';

class CardsStore {
  playerCards = [];

  isDarkCardOpened = false;

  dealerCardsInfo = {
    point: null,
    cards: null,
  };

  isShuffle = false;

  cardsTotalPointByHand = {};

  isCuttingCardOpen = false;

  openedCardsCount = null;

  cardInCentralBox = null;

  cuttingCardPlace = null;

  selectedCardType = 'calode-1';

  shuffleAnimationType = '';

  selectedCardBackSideType = 'backside-1';

  timeoutIds = [];

  constructor() {
    makeObservable(this, {
      isShuffle: observable,
      cuttingCardPlace: observable,
      isDarkCardOpened: observable,
      openedCardsCount: observable,
      isCuttingCardOpen: observable,
      selectedCardType: observable,
      cardInCentralBox: observable,
      shuffleAnimationType: observable,
      selectedCardBackSideType: observable,
      playerCards: observable.deep,
      dealerCardsInfo: observable.deep,
      cardsTotalPointByHand: observable.deep,
      setPlayerCards: action.bound,
      spliceUserCard: action.bound,
      setIsShuffle: action.bound,
      // removeCardsPoints: action.bound,
      // spliceDealerCards: action.bound,
      setCardsTotalPointByHand: action.bound,
      setDealerCardsInfo: action.bound,
      setIsDarkCardOpened: action.bound,
      setCardInCentralBox: action.bound,
      handleUpdateCardCentralBox: action.bound,
      setOpenedCardsCount: action.bound,
      setCuttingCardPlace: action.bound,
      setFirstSplitCard: action.bound,
      setSelectedCardType: action.bound,
      setIsCuttingCardOpen: action.bound,
      setShuffleAnimationType: action.bound,
      setSelectedCardBackSideType: action.bound,
      resetCards: action.bound,
    });
  }

  scheduleTimeout(callback, delay) {
    const id = setTimeout(() => {
      runInAction(callback);
      this.timeoutIds = this.timeoutIds.filter((tid) => tid !== id);
    }, delay);
    this.timeoutIds.push(id);
  }

  clearAllTimeouts() {
    this.timeoutIds.forEach(clearTimeout);
    this.timeoutIds = [];
  }

  setIsCuttingCardOpen(arg) {
    this.isCuttingCardOpen = arg;
  }

  // removeCardsPoints() {
  //   this.cardsTotalPointByHand = {};
  // }

  setCardsTotalPointByHand({ point, secondPoint, handType }) {
    this.cardsTotalPointByHand = {
      ...this.cardsTotalPointByHand,
      [handType]: { point, secondPoint },
    };
  }

  setCuttingCardPlace(arg) {
    this.cuttingCardPlace = arg;
  }

  setOpenedCardsCount(count) {
    this.openedCardsCount = count;
  }

  setDealerCardsInfo(dealerCardsInfo) {
    this.dealerCardsInfo = dealerCardsInfo;
  }

  setFirstSplitCard() {
    this.spliceUserCard(1, { ...this.playerCards[1], handType: CARD_HAND_TYPES.secondHand });
  }

  spliceUserCard(cardIndex, newCard) {
    this.playerCards.splice(cardIndex, 1, newCard);
    this.setPlayerCards([...this.playerCards]);
  }

  // spliceDealerCards(cardIndex, newCard) {
  //   this.dealerCardsInfo.cards.splice(cardIndex, 1, newCard);
  //   this.setDealerCardsInfo({ ...this.dealerCardsInfo });
  // }

  updateCards({
    openedCard,
    point,
    isDouble = false,
    hasBlackjack = false,
    secondPoint,
    callBack,
  }) {
    if (openedCard.card === CUTTING_CARD_TYPE) {
      this.setIsCuttingCardOpen(true);
      this.handleUpdateCardCentralBox(openedCard);
      return;
    }
    if (openedCard.owner === CARD_OWNER_TYPE.dealer) {
      this.updateDealerCardsInfo({ openedCard, point, hasBlackjack, secondPoint });
    } else {
      this.updatePlayerCards({
        openedCard,
        isDouble,
        callBack,
        hasBlackjack,
      });
    }
  }

  setPlayerCards(cards) {
    this.playerCards = cards;
  }

  setCardInCentralBox(card) {
    this.cardInCentralBox = card;
  }

  setIsDarkCardOpened(bool) {
    this.isDarkCardOpened = bool;
  }

  handleUpdateCardCentralBox(card) {
    this.setCardInCentralBox({ ...card, cardInCentral: true });
    this.scheduleTimeout(() => {
      this.setCardInCentralBox({ ...card, cardInCentral: true, hide: true });
    }, 800);
    this.scheduleTimeout(() => {
      this.setCardInCentralBox(null);
    }, 1500);
  }

  updatePlayerCards({ openedCard, callBack, isDouble, hasBlackjack }) {
    this.setPlayerCards([...this.playerCards, { ...openedCard, isDouble }]);
    if (userSettingsStore.isAnimationOn) {
      this.handleUpdateCardCentralBox(openedCard);
    }
    const openedCardIndex = this.playerCards.findIndex((item) => item.index === openedCard.index);
    this.scheduleTimeout(() => {
      this.spliceUserCard(openedCardIndex, {
        isDouble,
        hasBlackjack,
        ...openedCard,
      });
    }, 800);
    this.scheduleTimeout(() => {
      roundActiveHandStore.setHandInfo({
        changedProps: {
          [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isBlackJack]]:
            hasBlackjack,
          [DECISION_ACTION_TYPES_FOR_STORE[DECISION_ACTION_NAMES_FOR_STORE.isHandActive]]: true,
        },
      });
      this.spliceUserCard(openedCardIndex, {
        showCard: true,
        isDouble,
        hasBlackjack,
        ...openedCard,
      });
      callBack && callBack();
    }, 1500);
  }

  updateDealerCardsInfo({ openedCard, point, hasBlackjack, secondPoint }) {
    const { cards } = this.dealerCardsInfo;
    const darkCardIndex = cards?.findIndex((card) => card.index === openedCard.index);
    if (darkCardIndex > -1 && cards?.length > darkCardIndex) {
      this.setIsDarkCardOpened(true);
      const changedCards = [...cards];
      changedCards.splice(darkCardIndex, 1, openedCard);
      this.setDealerCardsInfo({ cards: [...changedCards], point, secondPoint, hasBlackjack });
    } else {
      const updatedDealerCards = cards ? [...cards, openedCard] : [openedCard];
      this.setDealerCardsInfo({ point, cards: updatedDealerCards, hasBlackjack, secondPoint });
    }
  }

  setSelectedCardBackSideType(type) {
    this.selectedCardBackSideType = type;
  }

  setSelectedCardType(type) {
    this.selectedCardType = type;
  }

  setShuffleAnimationType(type) {
    this.shuffleAnimationType = type;
  }

  setIsShuffle(bool) {
    this.isShuffle = bool;
  }

  resetCards() {
    this.clearAllTimeouts();
    this.cuttingCardPlace = null;
    this.playerCards = [];
    this.isDarkCardOpened = false;
    this.dealerCardsInfo = {
      point: null,
      cards: null,
    };
    this.isShuffle = false;
    this.cardsTotalPointByHand = {};
    this.isCuttingCardOpen = false;
    this.openedCardsCount = null;
    this.cardInCentralBox = null;
    this.selectedCardType = 'calode-1';
    this.shuffleAnimationType = '';
    this.selectedCardBackSideType = 'backside-1';
  }
}

export const cardsStore = new CardsStore();
